const API_BASE = '/api';

interface TokenStore {
    accessToken: string | null;
    refreshToken: string | null;
}

const tokens: TokenStore = {
    accessToken: localStorage.getItem('arcanum-access-token'),
    refreshToken: localStorage.getItem('arcanum-refresh-token'),
};

export function setTokens(accessToken: string, refreshToken: string) {
    tokens.accessToken = accessToken;
    tokens.refreshToken = refreshToken;
    localStorage.setItem('arcanum-access-token', accessToken);
    localStorage.setItem('arcanum-refresh-token', refreshToken);
}

export function clearTokens() {
    tokens.accessToken = null;
    tokens.refreshToken = null;
    localStorage.removeItem('arcanum-access-token');
    localStorage.removeItem('arcanum-refresh-token');
}

export function getAccessToken(): string | null {
    return tokens.accessToken;
}

async function refreshAccessToken(): Promise<boolean> {
    if (!tokens.refreshToken) {
        return false;
    }

    try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: tokens.refreshToken }),
        });

        if (!res.ok) {
            return false;
        }

        const data = (await res.json()) as { accessToken: string; refreshToken: string };
        setTokens(data.accessToken, data.refreshToken);
        return true;
    } catch {
        return false;
    }
}

export class ApiError extends Error {
    constructor(
        public statusCode: number,
        message: string,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

async function request<T>(
    path: string,
    options: RequestInit = {},
    retry = true,
): Promise<T> {
    const headers: Record<string, string> = {
        ...(options.headers as Record<string, string>),
    };

    if (options.body && !(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }

    if (tokens.accessToken) {
        headers['Authorization'] = `Bearer ${tokens.accessToken}`;
    }

    const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers,
    });

    // If unauthorized, try refreshing token
    if (res.status === 401 && retry && tokens.refreshToken) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
            return request<T>(path, options, false);
        }
        // Refresh failed - clear tokens and redirect to login
        clearTokens();
        window.location.href = '/login';
        throw new ApiError(401, 'Session expired');
    }

    if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new ApiError(res.status, body.message ?? `Request failed with status ${res.status}`);
    }

    // Handle empty responses (204)
    if (res.status === 204) {
        return undefined as T;
    }

    return res.json() as Promise<T>;
}

// ========== Auth ==========
export const authApi = {
    register(data: { name: string; email: string; password: string; country?: string; acceptTerms: boolean }) {
        return request<{
            user: {
                id: string;
                name: string;
                email: string;
                country: string | null;
                role: 'user' | 'admin';
                status: 'active' | 'disabled';
                sapConfig: { host: string; user: string; client: string } | null;
                createdAt: string;
                updatedAt: string;
            };
            tokens: { accessToken: string; refreshToken: string };
        }>('/auth/register', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    login(data: { email: string; password: string }) {
        return request<{
            user: {
                id: string;
                name: string;
                email: string;
                country: string | null;
                role: 'user' | 'admin';
                status: 'active' | 'disabled';
                sapConfig: { host: string; user: string; client: string } | null;
                subscriptions: string[];
                adminGrantedPlans: string[];
                createdAt: string;
                updatedAt: string;
            };
            tokens: { accessToken: string; refreshToken: string };
        }>('/auth/login', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },
};

// ========== User ==========
export const userApi = {
    getProfile() {
        return request<{
            user: {
                id: string;
                name: string;
                email: string;
                country: string | null;
                role: 'user' | 'admin';
                status: 'active' | 'disabled';
                sapConfig: { host: string; user: string; client: string } | null;
                subscriptions: string[];
                adminGrantedPlans: string[];
                createdAt: string;
                updatedAt: string;
            };
        }>('/users/me');
    },

    updateProfile(data: { name?: string; country?: string }) {
        return request<{
            id: string;
            name: string;
            email: string;
            country: string | null;
            sapConfig: { host: string; user: string; client: string } | null;
            createdAt: string;
            updatedAt: string;
        }>('/users/me', {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    updateSapConfig(data: { host: string; user: string; password: string; client: string }) {
        return request<{ message: string }>('/users/me/sap-config', {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    changePassword(password: string) {
        return request<{ message: string }>('/users/me/password', {
            method: 'PATCH',
            body: JSON.stringify({ password }),
        });
    },

    canSubscribe() {
        return request<{ allowed: boolean }>('/users/me/can-subscribe');
    },
};

// ========== API Keys ==========
export const apiKeysApi = {
    list() {
        return request<{
            keys: Array<{
                id: string;
                name: string;
                keyPrefix: string;
                maskedKey: string;
                status: 'active' | 'revoked';
                planId: string;
                createdAt: string;
                revokedAt: string | null;
            }>;
        }>('/api-keys');
    },

    generate(data: { name: string; planId: string }) {
        return request<{
            apiKey: {
                id: string;
                name: string;
                keyPrefix: string;
                maskedKey: string;
                status: 'active' | 'revoked';
                planId: string;
                createdAt: string;
                revokedAt: string | null;
            };
            fullKey: string;
        }>('/api-keys', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    revoke(id: string) {
        return request<{ message: string }>(`/api-keys/${id}/revoke`, {
            method: 'PATCH',
        });
    },
};

// ========== Payments ==========
export const paymentsApi = {
    createCheckout(data: { planId: string }) {
        return request<{ checkoutUrl: string }>('/payments/create-checkout', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    createPortal() {
        return request<{ portalUrl: string }>('/payments/create-portal', {
            method: 'POST',
            body: JSON.stringify({}),
        });
    },
};

// ========== Subscriptions ==========
export const subscriptionsApi = {
    list() {
        return request<{
            subscriptions: Array<{
                id: string;
                userId: string;
                planId: string;
                status: string;
                startDate: string;
                endDate: string | null;
                price: number;
                currency: string;
                createdAt: string;
                updatedAt: string;
            }>;
        }>('/subscriptions');
    },
};

// ========== SAP Environments ==========
interface SapEnvironmentResponse {
    id: string;
    identifier: string;
    description: string;
    host: string;
    client: string;
    username: string;
    language: string;
    createdAt: string;
    updatedAt: string;
}

export const sapEnvironmentsApi = {
    list() {
        return request<{ environments: SapEnvironmentResponse[] }>('/sap-environments');
    },

    create(data: {
        identifier: string;
        description: string;
        host: string;
        client: string;
        username: string;
        password: string;
        language?: string;
    }) {
        return request<{ environment: SapEnvironmentResponse }>('/sap-environments', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    update(id: string, data: {
        identifier?: string;
        description?: string;
        host?: string;
        client?: string;
        username?: string;
        password?: string;
        language?: string;
    }) {
        return request<{ environment: SapEnvironmentResponse }>(`/sap-environments/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    delete(id: string) {
        return request<void>(`/sap-environments/${id}`, { method: 'DELETE' });
    },
};

// ========== Admin ==========
import type { AdminUserView } from '@/domain/entities/user';

export interface AllowedDomainView {
    id: string;
    domain: string;
    createdAt: string;
}

export const adminApi = {
    listUsers() {
        return request<{ users: AdminUserView[] }>('/admin/users');
    },

    createUser(data: {
        name: string;
        email: string;
        password: string;
        country: string;
        role: 'user' | 'admin';
    }) {
        return request<{
            user: {
                id: string;
                name: string;
                email: string;
                country: string | null;
                role: 'user' | 'admin';
                status: 'active' | 'disabled';
                createdAt: string;
                updatedAt: string;
            };
        }>('/admin/users', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    toggleRole(userId: string) {
        return request<{ id: string; role: 'user' | 'admin' }>(`/admin/users/${userId}/role`, {
            method: 'PATCH',
        });
    },

    toggleStatus(userId: string) {
        return request<{ id: string; status: 'active' | 'disabled' }>(`/admin/users/${userId}/status`, {
            method: 'PATCH',
        });
    },

    resetPassword(userId: string, password: string) {
        return request<{ id: string; message: string }>(`/admin/users/${userId}/password`, {
            method: 'PATCH',
            body: JSON.stringify({ password }),
        });
    },

    listDomains() {
        return request<{ domains: AllowedDomainView[] }>('/admin/domains');
    },

    addDomain(domain: string) {
        return request<AllowedDomainView>('/admin/domains', {
            method: 'POST',
            body: JSON.stringify({ domain }),
        });
    },

    removeDomain(id: string) {
        return request<void>(`/admin/domains/${id}`, { method: 'DELETE' });
    },

    grantPlan(userId: string, planId: string) {
        return request<{ id: string; userId: string; planId: string; status: string }>(
            `/admin/users/${userId}/grant-plan`,
            { method: 'POST', body: JSON.stringify({ planId }) },
        );
    },

    revokePlan(userId: string, planId: string) {
        return request<{ id: string; userId: string; planId: string; status: string }>(
            `/admin/users/${userId}/revoke-plan`,
            { method: 'PATCH', body: JSON.stringify({ planId }) },
        );
    },
};
